/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable @typescript-eslint/no-unused-vars */
import "rsuite/DatePicker/styles/index.css";

import { useEffect, useState, useMemo } from "react";
import { DateRangePicker } from "rsuite";
import { SubmitButton as Button } from "~/components/form/submit-button";
import {
  getReports,
  getAdminMenuItems,
  getClientMenuItems,
  getSupervisorMenuItems,
  getBusinessRulesMenuItems,
} from "~/services/supervisor.services";

import ReportTable from "./table";
import { Timeout } from "node_modules/@tanstack/react-router/dist/esm/utils";
import { ISidebarMenuItem } from "~/types/agent-types";
import { reportsRoute } from "~/pages/all-routes";
import { convertSecondsToHHMMSS } from "~/utils/basic";
import { toast } from "react-toastify";
import StatusCounters from "./statusCounter";
import DynamicFilters from "./dynamicFilters";
import DynamicButtonFilters from "./dynamicButtonFilters";

type CustomResult = ISidebarMenuItem & {
  schema?: {
    result?: Array<{
      fields: Array<{ id: string; label: string }>;
    }>;
  };
};
const ReportComponent = () => {
  const { name } = reportsRoute.useSearch();
  const [sidebarItems, setSidebarItems] = useState<ISidebarMenuItem[]>([]);
  const [worklistData, setWorklistData] = useState<any[]>([]);
  const [tableFields, setTableFields] = useState<string[]>([]);

  const today = new Date();
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    new Date(new Date().setDate(today.getDate() - 1)),
    today,
  ]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [statusFilter, setStatusFilter] = useState<string>("All");
  const [cutoffFilter, setCutoffFilter] = useState<string | null>(null);

  const [statusCheckInterval, setStatusCheckInterval] = useState<
    number | null | Timeout
  >(null);
  const [intervalClearTimeOut, setIntervalClearTimeOut] = useState<number>(0);

  const handleDateRangeChange = (value: [Date | null, Date | null]) => {
    setDateRange(value);
  };

  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const reportType = name;
  const getReportName = (reportType: string) => {
    // if (reportType === "invoice_accuracy") return "Invoice Accuracy Report";
    // if (reportType === "header_accuracy") return "Header Accuracy Report";
    if (reportType === "output_files") return "Output Files";
    if (reportType === "milestones_dashboard") return "Milestones Dashboard";
    if (reportType === "reference_accuracy") return "Reference Number Accuracy Report";
    if (reportType === "accessorial_accuracy") return "Accessorials Accuracy Report";
    if (reportType === "status_dashboard") return "Status Dashboard";
    if (reportType === "daily_report_files") return "Daily Report Files";
    if (reportType === "accuracy_report_files") return "Accuracy Report Files";
    // return "Accuracy Report";
    return reportType.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) + " Report";
  };

  // Memoize the resultSchema from sidebarItems and reportType
  const resultSchema = useMemo(() => {
    const matched = sidebarItems
      .flatMap((item) => item.subMenu || [])
      .find((sub) => sub.id === reportType) as CustomResult | undefined;
    console.log("Kiran ", matched?.schema?.result);
    return matched?.schema?.result?.[0]?.fields || [];
  }, [sidebarItems, reportType]);

  const formatDate = (date: Date | string) => {
    if (typeof date === "string") return date;
    return date.toLocaleDateString("en-GB"); // dd/MM/yyyy
  };

  const fetchData = async (
    fromDate: Date | string | null,
    toDate: Date | string | null
  ) => {
    if (!fromDate || !toDate) return;

    setIsLoading(true);
    setIsDisabled(true);

    const fromStr = formatDate(fromDate);
    const toStr = formatDate(toDate);

    console.log(fromStr, toStr);

    const queryString = `name=${name}&fromdate=${fromStr}&todate=${toStr}`;

    try {
      const scheduledReportsResponse = await getReports(
        `scheduled-reports?${queryString}`
      );

      if (scheduledReportsResponse.data.data !== "success") {
        console.error("Failed to fetch scheduled reports");
        setIsLoading(false);
        setIsDisabled(false);
        return;
      }

      const intervalId = setInterval(async () => {
        try {
          const statusResponse = await getReports(
            `scheduled-reports/status?name=${name}`
          );

          const status = statusResponse.data.data;
          console.log(status);

          if (status === "Complete") {
            clearInterval(intervalId);

            const dataResponse = await getReports(
              `scheduled-reports/data?name=${name}`
            );

            if (!dataResponse.status) {
              console.error("Failed to fetch reports");
              return;
            }

            const response = await fetch(dataResponse.data.data);
            const jsonData = await response.json();

            const tableData =
              jsonData?.dbdata?.report?.reportentity1 ||
              jsonData?.dbdata?.report?.reportentity2 ||
              jsonData?.dbdata?.report?.reportentity3 ||
              [];

            const fieldMapping = resultSchema.reduce(
              (
                acc: Record<string, string>,
                field: { id: string; label: string }
              ) => {
                acc[field.id] = field.label;
                return acc;
              },
              {}
            );

            const keysToConvert = ["aging", "overalltat"];

            const mappedTableData = tableData.map((row: any) => {
              const mappedRow: Record<string, any> = {};

              Object.entries(row).forEach(([key, value]) => {
                if (keysToConvert.includes(key)) {
                  mappedRow[fieldMapping[key]] =
                    convertSecondsToHHMMSS(Number(value));
                } else {
                  mappedRow[fieldMapping[key] || key] = value;
                }
              });

              return mappedRow;
            });

            const fields = resultSchema.map((f: any) => f.label);

            setTableFields(fields);
            setWorklistData(mappedTableData);

            setIsLoading(false);
            setIsDisabled(false);
          }

          if (status === "In-Process") {
            setIntervalClearTimeOut((prev) => prev + 1);
          }
        } catch (err) {
          console.error("Error in polling:", err);
          clearInterval(intervalId);
          setIsLoading(false);
          setIsDisabled(false);
        }
      }, 5000);

      setStatusCheckInterval(intervalId);
    } catch (error) {
      console.error("Error fetching reports:", error);
      setIsLoading(false);
      setIsDisabled(false);
    }
  };

  // --- Count helpers ---
  function countByStatus(data: any[]): Record<string, number> {
    return data.reduce((acc, item) => {
      const status = item?.Status;
      if (!status) return acc;
      acc["All"] = (acc["All"] || 0) + 1;
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
  }

  // cutoff filter
  function countByCutoff(data: any[]): Record<string, number> {
    return data.reduce((acc, item) => {
      const cutoff = item?.cutoff;
      if (!cutoff) return acc;
      acc[cutoff] = (acc[cutoff] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
  }

  const statusCounts = useMemo(
    () => countByStatus(worklistData),
    [worklistData]
  );

  const statusFilteredData = useMemo(() => {
    if (statusFilter === "All") return worklistData;
    return worklistData.filter((item) => item?.Status === statusFilter);
  }, [worklistData, statusFilter]);

  const cutoffCounts = useMemo(
    () => countByCutoff(statusFilteredData),
    [statusFilteredData]
  );

  const filteredTableData = useMemo(() => {
    if (!cutoffFilter) return statusFilteredData;
    return statusFilteredData.filter((item) => item?.cutoff === cutoffFilter);
  }, [statusFilteredData, cutoffFilter]);

  const handleStatusFilterChange = (filter: string) => {
    setStatusFilter(filter);
    setCutoffFilter(null); // reset cutoff when status changes
  };

  const handleCutoffFilterChange = (filter: string) => {
    setCutoffFilter((prev) => (prev === filter ? null : filter));
  };

  const handleRefreshData = async () => {
    setStatusFilter("All");
    setCutoffFilter(null);
    await fetchData(dateRange[0], dateRange[1]);
  };

  const fetchDataOnPageLoad = async (
    from: Date | string,
    to: Date | string
  ) => {
    setIsLoading(true);
    try {
      const fromStr = typeof from === "string" ? from : formatDate(from);
      const toStr = typeof to === "string" ? to : formatDate(to);
      await fetchData(fromStr, toStr);
      console.log("Initial data fetch complete");
    } finally {
      //setIsLoading(false);
    }
  };

  // Fetch sidebar items on mount
  useEffect(() => {
    const roleid = localStorage.getItem("roleid");

    async function fetchSidebarItems() {
      let response;
      switch (roleid) {
        case "12":
          response = await getAdminMenuItems("admin_menu_items");
          break;
        case "8":
          response = await getClientMenuItems("client_menu_items");
          break;
        case "16":
          response = await getBusinessRulesMenuItems("business_rule_menu_items");
          break;
        case "3":
        default:
          response = await getSupervisorMenuItems("supervisor_menu_items");
          break;
      }

      if (response?.status) {
        const items = response.data?.data?.menu_items ?? response.data ?? [];
        setSidebarItems(items);
      } else {
        setSidebarItems([]);
      }
    }

    fetchSidebarItems();
  }, []);

  useEffect(() => {
    if (reportType === "status_dashboard" && sidebarItems.length > 0) {
      const fmt = (d: Date) => d.toLocaleDateString("en-GB");
      const todayD = new Date();
      const yesterday = new Date();
      yesterday.setDate(todayD.getDate() - 1);
      fetchDataOnPageLoad(fmt(yesterday), fmt(todayD));
      setIsLoading(true);
    }
  }, [reportType, sidebarItems]);

  // Reset when report type changes
  useEffect(() => {
    setWorklistData([]);
    setStatusFilter("All");
    setCutoffFilter(null);
    setDateRange([
      new Date(new Date().setDate(today.getDate() - 1)),
      today,
    ] as [Date | null, Date | null]);
    setIsDisabled(false);
  }, [name]);

  useEffect(() => {
    return () => {
      if (statusCheckInterval) clearInterval(statusCheckInterval);
    };
  }, [statusCheckInterval]);

  useEffect(() => {
    if (intervalClearTimeOut >= 120 && statusCheckInterval) {
      clearInterval(statusCheckInterval);
      setIsLoading(false);
      setIsDisabled(false);
      setIntervalClearTimeOut(0);
      toast.error("Something went wrong, Please try again", { theme: "colored" });
    }
  }, [intervalClearTimeOut]);

  const isStatusDashboard = reportType === "status_dashboard";
  
  return (
    <div>
      <div className="mb-4">
        <div className="flex justify-between items-center mb-3">
          <h4 className="text-2xl font-semibold text-orange-500">
            {getReportName(name)}
          </h4>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex flex-col">
              <DateRangePicker
                showOneCalendar
                onChange={handleDateRangeChange as any}
                format="dd/MM/yyyy"
                value={dateRange as any}
                placeholder="Please Select the date range"
                disabled={isDisabled}
              />
            </div>
            <Button
              className="h-10 ml-4"
              role="button"
              variant="primary"
              size="small"
              onClick={async () => fetchData(dateRange[0], dateRange[1])}
              disabled={isDisabled}
            >
              Search
            </Button>
          </div>
        </div>
      </div>

      {isStatusDashboard && <StatusCounters />}

      {isStatusDashboard && !isLoading && worklistData.length > 0 && (
        <>
          <DynamicFilters
            data={statusCounts}
            onFilterChange={handleStatusFilterChange}
            activeFilter={statusFilter}
          />
          <DynamicButtonFilters
            data={cutoffCounts}
            onFilterChange={handleCutoffFilterChange}
            activeFilter={cutoffFilter}
          />
        </>
      )}

      <div className="bg-neutral-100 rounded-lg">
        <div className="p-2">
          {isLoading && name === reportType ? (
            <div className="flex flex-col items-center justify-center h-48">
              <div className="spinner-border animate-spin border-4 border-orange-400 border-t-transparent w-12 h-12 rounded-full"></div>
              <p className="mt-2 text-lg">
                Please wait...
                <br />
                Generating reports may take some while
              </p>
            </div>
          ) : worklistData.length > 0 ? (
            <ReportTable
              data={isStatusDashboard ? filteredTableData : worklistData}
              fields={tableFields}
              reportname={name}
              refreshData={handleRefreshData}
            />
          ) : (
            <p className="text-center text-lg font-bold">
              No data available for the selected dates
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
export default ReportComponent;
